"use client";
// src/components/profile/RetailReviewSubmitForm.tsx

import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Star, ShieldCheck, CheckCircle2, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { submitRetailReviewAction } from "@/app/actions/reviews";
import { RETAIL_REVIEW_TAGS } from "@/types/retailReview";
import type { RetailReviewTag } from "@/types/retailReview";

interface RetailReviewSubmitFormProps {
  profileId: string;
  profileOwnerName: string;
}

const tagLabels: Record<RetailReviewTag, string> = {
  great_product: "Great product",
  good_value: "Good value",
  fast_processing: "Fast processing",
  great_communication: "Great communication",
  accurate_description: "Accurate description",
  smooth_delivery: "Smooth delivery / collection",
};

export function RetailReviewSubmitForm({ profileId, profileOwnerName }: RetailReviewSubmitFormProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const orderLinkToken = searchParams?.get("rtoken") || "";
  const orderLinkId = searchParams?.get("rorder") || "";
  const hasOrderLink = Boolean(orderLinkToken);

  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [body, setBody] = useState("");
  const [reviewerName, setReviewerName] = useState("");
  const [reviewerEmail, setReviewerEmail] = useState(searchParams?.get("remail") || "");
  const [purchasedProductName, setPurchasedProductName] = useState("");
  const [tags, setTags] = useState<RetailReviewTag[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const toggleTag = (tag: RetailReviewTag) => {
    setTags((current) =>
      current.includes(tag) ? current.filter((item) => item !== tag) : [...current, tag],
    );
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (rating === 0) {
      setResult({ success: false, message: "Please choose a star rating." });
      return;
    }
    if (!reviewerName.trim()) {
      setResult({ success: false, message: "Please enter your first name." });
      return;
    }
    if (!reviewerEmail.trim() && !orderLinkToken) {
      setResult({
        success: false,
        message: "We verify buyers before reviews go live. Enter the email you used at checkout, or use your PayFast order link.",
      });
      return;
    }
    setSubmitting(true);
    setResult(null);
    const res = await submitRetailReviewAction(profileId, {
      rating,
      body,
      reviewerName: reviewerName.trim(),
      reviewerEmail: reviewerEmail.trim() || undefined,
      guestAccessToken: orderLinkToken || undefined,
      purchasedProductName: purchasedProductName.trim() || undefined,
      tags,
    });
    setResult(res);
    setSubmitting(false);
    if (res.success) {
      setRating(0);
      setBody("");
      setReviewerName("");
      setPurchasedProductName("");
      setTags([]);
      router.refresh();
    }
  }

  if (result?.success) {
    return (
      <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-800">
        <p className="flex items-center gap-2 font-semibold">
          <CheckCircle2 className="h-4 w-4" /> {result.message}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Only verified buyers who completed a paid order can review this shop. Leave your name and the email you
        used at checkout — your purchase is confirmed against the order without revealing your email publicly.
      </p>

      {hasOrderLink && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/70 p-3 text-xs text-emerald-800">
          <ShieldCheck className="h-4 w-4 shrink-0" />
          <span>
            Verified order link detected{orderLinkId ? ` for order #${orderLinkId.slice(0, 8).toUpperCase()}` : ""}.
            Your email is confirmed — no extra proof needed.
          </span>
        </div>
      )}

      <div>
        <p className="mb-1 text-sm font-medium text-foreground">Your rating for {profileOwnerName}</p>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHovered(star)}
              onMouseLeave={() => setHovered(0)}
              className="focus:outline-none"
              aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
            >
              <Star
                className={`h-7 w-7 transition-colors ${
                  star <= (hovered || rating) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
                }`}
              />
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="retail-reviewer-name">Your first name *</Label>
          <Input
            id="retail-reviewer-name"
            placeholder="e.g. Thabo M"
            value={reviewerName}
            onChange={(e) => setReviewerName(e.target.value)}
            maxLength={40}
            required
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="retail-reviewer-email">Email used at checkout{hasOrderLink ? " (optional)" : " *"}</Label>
          <Input
            id="retail-reviewer-email"
            type="email"
            placeholder="you@example.com"
            value={reviewerEmail}
            onChange={(e) => setReviewerEmail(e.target.value)}
            disabled={hasOrderLink}
          />
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="retail-review-product">What did you buy? (optional)</Label>
        <Input
          id="retail-review-product"
          placeholder="e.g. Linen Midi Dress"
          value={purchasedProductName}
          onChange={(e) => setPurchasedProductName(e.target.value)}
          maxLength={120}
        />
      </div>

      <div className="rounded-lg border border-dashed border-slate-300 p-3">
        <p className="flex items-center gap-1.5 text-sm font-medium text-foreground">
          <Package className="h-4 w-4 text-pink-500" />
          What was great? (optional)
        </p>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {RETAIL_REVIEW_TAGS.map((tag) => (
            <label key={tag} className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={tags.includes(tag)}
                onChange={() => toggleTag(tag)}
                disabled={submitting}
              />
              <span>{tagLabels[tag]}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="retail-review-body">Your review</Label>
        <Textarea
          id="retail-review-body"
          placeholder="How was the product and the shop? Would you buy from them again?"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={500}
          rows={4}
          required
        />
        <p className="text-right text-xs text-muted-foreground">{body.length}/500</p>
      </div>

      {result && !result.success && <p className="text-sm text-destructive">{result.message}</p>}

      <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
        {submitting ? "Verifying & submitting…" : "Submit Verified Review"}
      </Button>
    </form>
  );
}